import React, { useEffect } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useBillingStore from '../../stores/useBillingStore';
import { EnableOverageModal, EditOverageLimitModal } from '../billing/OverageModals';
import useOverageModals from '../billing/useOverageModals';
import { formatDate, formatDollarsFromCents } from '../billing/billingFormatters';

export default function CreditsExhaustedDialog({ open, onClose, message, reason }) {
  const navigate = useNavigate();
  const subscription = useBillingStore((s) => s.subscription);
  const overageSettings = useBillingStore((s) => s.overageSettings);
  const fetchOverageSettings = useBillingStore((s) => s.fetchOverageSettings);

  const modals = useOverageModals({ onSuccess: onClose });

  useEffect(() => {
    if (open && subscription?.plan === 'STANDARD') {
      fetchOverageSettings();
    }
  }, [open, subscription?.plan, fetchOverageSettings]);

  const plan = subscription?.plan;
  const overageAllowed = subscription?.overage?.allowed;
  const overageEnabled = subscription?.overage?.enabled;
  const spendLimitCents = subscription?.overage?.spendLimitCents;

  const isSpendLimit = reason === 'SPEND_LIMIT_REACHED';
  const isStandardOverageOff = plan === 'STANDARD' && overageAllowed && !overageEnabled;

  let title = "You've used all your included credits";
  let body =
    message ||
    'Your organization has no remaining credits for this action. Contact support@workflow86.com to upgrade or add credits.';
  let primaryLabel = 'View plans';
  let primaryAction = () => {
    onClose();
    navigate('/pricing');
  };
  let secondaryLabel = 'Cancel';
  let secondaryAction = onClose;

  if (isSpendLimit) {
    title = 'On-demand spending limit reached';
    body = `You've used ${formatDollarsFromCents(spendLimitCents)} of on-demand usage this month. Raise your limit or wait until ${formatDate(subscription?.currentPeriodEnd)} for new included credits.`;
    primaryLabel = 'Edit spending limit';
    primaryAction = modals.openEdit;
    secondaryLabel = 'Cancel';
    secondaryAction = onClose;
  } else if (isStandardOverageOff) {
    body =
      'Enable on-demand usage to keep going, or upgrade your plan for more included credits each month.';
    primaryLabel = 'Enable on-demand usage';
    primaryAction = modals.openEnable;
    secondaryLabel = 'View plans';
    secondaryAction = () => {
      onClose();
      navigate('/pricing');
    };
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{body}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={secondaryAction} sx={{ textTransform: 'none' }}>
            {secondaryLabel}
          </Button>
          <Button variant="contained" onClick={primaryAction} sx={{ textTransform: 'none' }}>
            {primaryLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <EnableOverageModal
        open={modals.enableOpen}
        onClose={() => !modals.actionLoading && modals.setEnableOpen(false)}
        onConfirm={modals.runUpdate}
        defaultSpendLimitCents={overageSettings?.spendLimitCents || 5000}
        chargeIncrementCents={overageSettings?.chargeIncrementCents || 2500}
        microDollarsPerCredit={overageSettings?.microDollarsPerCredit || 1000}
        loading={modals.actionLoading}
        error={modals.actionError}
      />

      <EditOverageLimitModal
        open={modals.editOpen}
        onClose={() => !modals.actionLoading && modals.setEditOpen(false)}
        onConfirm={modals.runUpdate}
        currentLimitCents={spendLimitCents}
        loading={modals.actionLoading}
        error={modals.actionError}
      />
    </>
  );
}
